import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { LogOut, Plus, MapPin, Clock, Users, Building2, Tag, Trash2, Pencil, UserPlus, Filter, X } from 'lucide-react';
import ComboboxClientes from '../components/ComboboxClientes';
import { Checkbox } from '../components/ui/checkbox';

const DashboardAdmin = () => {
  const { user, logout, API } = useAuth();
  const [acciones, setAcciones] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [searchCliente, setSearchCliente] = useState('');

  // Filtros de acciones
  const [filtroUsuario, setFiltroUsuario] = useState('todos');
  const [filtroCliente, setFiltroCliente] = useState('todos');
  const [filtroTipoAccion, setFiltroTipoAccion] = useState('todos');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [filtroPosibleCliente, setFiltroPosibleCliente] = useState('todos');

  const [showUserModal, setShowUserModal] = useState(false);
  const [showClienteModal, setShowClienteModal] = useState(false);
  const [showActionTypeModal, setShowActionTypeModal] = useState(false);
  const [showEditAccionModal, setShowEditAccionModal] = useState(false);
  const [showImportCSVModal, setShowImportCSVModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);

  const [newUser, setNewUser] = useState({ username: '', password: '', nombre: '', role: 'comercial' });
  const [newCliente, setNewCliente] = useState({ nombre: '' });
  const [newActionType, setNewActionType] = useState({ nombre: '' });
  const [editingAccion, setEditingAccion] = useState(null);

  // Filtrado de acciones en el frontend
  const accionesFiltradas = useMemo(() => {
    return acciones.filter(accion => {
      // Filtro por comercial
      if (filtroUsuario !== 'todos' && accion.user_id !== filtroUsuario) return false;
      
      // Filtro por cliente/posible cliente
      if (filtroPosibleCliente === 'cliente' && accion.es_posible_cliente) return false;
      if (filtroPosibleCliente === 'posible' && !accion.es_posible_cliente) return false;
      
      // Filtro por cliente específico
      if (filtroCliente !== 'todos' && !accion.es_posible_cliente && accion.cliente_id !== filtroCliente) return false;
      
      // Filtro por tipo de acción
      if (filtroTipoAccion !== 'todos' && accion.action_type_id !== filtroTipoAccion) return false;
      
      // Filtro por fecha desde
      if (filtroFechaDesde) {
        const fechaAccion = new Date(accion.fecha_hora);
        const fechaDesde = new Date(filtroFechaDesde);
        if (fechaAccion < fechaDesde) return false;
      }
      
      // Filtro por fecha hasta
      if (filtroFechaHasta) {
        const fechaAccion = new Date(accion.fecha_hora);
        const fechaHasta = new Date(filtroFechaHasta + 'T23:59:59');
        if (fechaAccion > fechaHasta) return false;
      }
      
      return true;
    });
  }, [acciones, filtroUsuario, filtroCliente, filtroTipoAccion, filtroFechaDesde, filtroFechaHasta, filtroPosibleCliente]);

  const limpiarFiltros = () => {
    setFiltroUsuario('todos');
    setFiltroCliente('todos');
    setFiltroTipoAccion('todos');
    setFiltroFechaDesde('');
    setFiltroFechaHasta('');
    setFiltroPosibleCliente('todos');
  };

  const hayFiltrosActivos = filtroUsuario !== 'todos' || filtroCliente !== 'todos' || 
    filtroTipoAccion !== 'todos' || filtroFechaDesde || filtroFechaHasta || filtroPosibleCliente !== 'todos';

  useEffect(() => {
    fetchData();
    fetchAcciones();
  }, []);

  const fetchData = async () => {
    try {
      const [usuariosRes, clientesRes, actionTypesRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/clientes`),
        axios.get(`${API}/action-types`)
      ]);
      setUsuarios(usuariosRes.data);
      setClientes(clientesRes.data);
      setActionTypes(actionTypesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  const fetchAcciones = async () => {
    try {
      const response = await axios.get(`${API}/acciones`);
      setAcciones(response.data);
    } catch (error) {
      toast.error('Error al cargar acciones');
    }
  };

  const handleCreateUser = async () => {
    try {
      await axios.post(`${API}/users`, newUser);
      toast.success('Usuario creado');
      setShowUserModal(false);
      setNewUser({ username: '', password: '', nombre: '', role: 'comercial' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('¿Eliminar este usuario?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleCreateCliente = async () => {
    try {
      await axios.post(`${API}/clientes`, newCliente);
      toast.success('Cliente creado');
      setShowClienteModal(false);
      setNewCliente({ nombre: '' });
      fetchData();
    } catch (error) {
      toast.error('Error al crear cliente');
    }
  };

  const handleDeleteCliente = async (clienteId) => {
    if (!window.confirm('¿Eliminar este cliente?')) return;
    try {
      await axios.delete(`${API}/clientes/${clienteId}`);
      toast.success('Cliente eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar cliente');
    }
  };

  const handleCreateActionType = async () => {
    try {
      await axios.post(`${API}/action-types`, newActionType);
      toast.success('Tipo de acción creado');
      setShowActionTypeModal(false);
      setNewActionType({ nombre: '' });
      fetchData();
    } catch (error) {
      toast.error('Error al crear tipo de acción');
    }
  };

  const handleDeleteActionType = async (actionTypeId) => {
    if (!window.confirm('¿Eliminar este tipo de acción?')) return;
    try {
      await axios.delete(`${API}/action-types/${actionTypeId}`);
      toast.success('Tipo de acción eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar tipo de acción');
    }
  };

  const handleImportCSV = async () => {
    if (!csvFile) {
      toast.error('Por favor selecciona un archivo CSV');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', csvFile);

    try {
      const response = await axios.post(`${API}/clientes/import-csv`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(`✓ Importación completada: ${response.data.clientes_importados} clientes añadidos`);
      if (response.data.clientes_duplicados > 0) {
        toast.info(`${response.data.clientes_duplicados} clientes duplicados omitidos`);
      }
      
      setShowImportCSVModal(false);
      setCsvFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al importar CSV');
    } finally {
      setImporting(false);
    }
  };

  const handleEditAccion = (accion) => {
    setEditingAccion(accion);
    setShowEditAccionModal(true);
  };

  const handleUpdateAccion = async () => {
    try {
      await axios.put(`${API}/acciones/${editingAccion.id}`, {
        cliente_id: editingAccion.es_posible_cliente ? null : editingAccion.cliente_id,
        es_posible_cliente: editingAccion.es_posible_cliente || false,
        posible_cliente_nombre: editingAccion.es_posible_cliente ? editingAccion.posible_cliente_nombre : null,
        action_type_id: editingAccion.action_type_id,
        duracion_horas: editingAccion.duracion_horas,
        duracion_minutos: editingAccion.duracion_minutos,
        descripcion: editingAccion.descripcion
      });
      toast.success('Acción actualizada');
      setShowEditAccionModal(false);
      fetchAcciones();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar acción');
    }
  };

  const handleDeleteAccion = async (accionId) => {
    if (!window.confirm('¿Eliminar esta acción comercial?')) return;
    try {
      await axios.delete(`${API}/acciones/${accionId}`);
      toast.success('Acción eliminada');
      fetchAcciones();
    } catch (error) {
      toast.error('Error al eliminar acción');
    }
  };

  const getBadgeClass = (actionTypeName) => {
    const lower = actionTypeName.toLowerCase();
    if (lower.includes('visita')) return 'badge-visit';
    if (lower.includes('llamada')) return 'badge-call';
    if (lower.includes('email')) return 'badge-email';
    return '';
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredClientes = clientes.filter(c => 
    c.nombre.toLowerCase().includes(searchCliente.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">FieldFlow Admin</h1>
            <p className="text-sm text-muted-foreground">Panel de Administración</p>
          </div>
          <Button variant="ghost" onClick={logout} data-testid="logout-button">
            <LogOut className="h-4 w-4 mr-2" />
            Salir
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-8">
        <Tabs defaultValue="acciones" className="space-y-6">
          <TabsList>
            <TabsTrigger value="acciones" data-testid="tab-acciones">Acciones Comerciales</TabsTrigger>
            <TabsTrigger value="usuarios" data-testid="tab-usuarios">Usuarios</TabsTrigger>
            <TabsTrigger value="clientes" data-testid="tab-clientes">Clientes</TabsTrigger>
            <TabsTrigger value="tipos" data-testid="tab-tipos">Tipos de Acción</TabsTrigger>
          </TabsList>

          <TabsContent value="acciones" className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-semibold">Todas las Acciones Comerciales</h2>
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-slate-500" />
                  <span className="text-sm text-slate-500">
                    {accionesFiltradas.length} de {acciones.length} registros
                  </span>
                  {hayFiltrosActivos && (
                    <Button variant="ghost" size="sm" onClick={limpiarFiltros} className="text-red-500 hover:text-red-700">
                      <X className="h-4 w-4 mr-1" />
                      Limpiar filtros
                    </Button>
                  )}
                </div>
              </div>

              {/* Panel de Filtros */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Comercial</Label>
                  <Select value={filtroUsuario} onValueChange={setFiltroUsuario}>
                    <SelectTrigger className="h-9" data-testid="filter-user-select">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      {usuarios.filter(u => u.role === 'comercial').map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Tipo Cliente</Label>
                  <Select value={filtroPosibleCliente} onValueChange={setFiltroPosibleCliente}>
                    <SelectTrigger className="h-9" data-testid="filter-cliente-type">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      <SelectItem value="cliente">Solo Clientes</SelectItem>
                      <SelectItem value="posible">Solo Posibles</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Cliente</Label>
                  <Select value={filtroCliente} onValueChange={setFiltroCliente}>
                    <SelectTrigger className="h-9" data-testid="filter-cliente-select">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      {clientes.slice(0, 100).map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Tipo Acción</Label>
                  <Select value={filtroTipoAccion} onValueChange={setFiltroTipoAccion}>
                    <SelectTrigger className="h-9" data-testid="filter-action-type">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="todos">Todos</SelectItem>
                      {actionTypes.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Desde</Label>
                  <Input 
                    type="date" 
                    value={filtroFechaDesde} 
                    onChange={(e) => setFiltroFechaDesde(e.target.value)}
                    className="h-9"
                    data-testid="filter-date-from"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-xs text-slate-600">Hasta</Label>
                  <Input 
                    type="date" 
                    value={filtroFechaHasta} 
                    onChange={(e) => setFiltroFechaHasta(e.target.value)}
                    className="h-9"
                    data-testid="filter-date-to"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Fecha</TableHead>
                      <TableHead>Comercial</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Duración</TableHead>
                      <TableHead>Descripción</TableHead>
                      <TableHead>Ubicación</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accionesFiltradas.map((accion) => (
                      <TableRow key={accion.id} className="table-row">
                        <TableCell className="table-cell">{formatDate(accion.fecha_hora)}</TableCell>
                        <TableCell className="table-cell font-medium">{accion.user_nombre}</TableCell>
                        <TableCell className="table-cell">
                          {accion.es_posible_cliente ? (
                            <span className="flex items-center gap-1">
                              <UserPlus className="h-3 w-3 text-blue-600" />
                              <span className="text-blue-600">{accion.posible_cliente_nombre}</span>
                              <Badge variant="outline" className="ml-1 text-xs">Posible</Badge>
                            </span>
                          ) : (
                            accion.cliente_nombre
                          )}
                        </TableCell>
                        <TableCell className="table-cell">
                          <Badge className={getBadgeClass(accion.action_type_nombre)}>
                            {accion.action_type_nombre}
                          </Badge>
                        </TableCell>
                        <TableCell className="table-cell">
                          <span className="flex items-center text-sm">
                            <Clock className="h-3 w-3 mr-1" />
                            {accion.duracion_horas}h {accion.duracion_minutos}m
                          </span>
                        </TableCell>
                        <TableCell className="table-cell">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="max-w-xs truncate cursor-help">
                                  {accion.descripcion}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-md p-4 bg-popover text-popover-foreground border shadow-lg">
                                <p className="text-sm whitespace-pre-wrap">{accion.descripcion}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell className="table-cell">
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${accion.lat},${accion.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="map-link inline-flex items-center text-accent"
                            data-testid={`map-link-${accion.id}`}
                          >
                            <MapPin className="h-4 w-4" />
                          </a>
                        </TableCell>
                        <TableCell className="table-cell">
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditAccion(accion)}
                              data-testid={`edit-action-admin-${accion.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAccion(accion.id)}
                              data-testid={`delete-action-admin-${accion.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {accionesFiltradas.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">
                    {hayFiltrosActivos ? 'No hay acciones que coincidan con los filtros' : 'No hay acciones registradas'}
                  </p>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="usuarios" className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold">Gestión de Usuarios</h2>
                <Dialog open={showUserModal} onOpenChange={setShowUserModal}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-user-button">
                      <Plus className="h-4 w-4 mr-2" />
                      Nuevo Usuario
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="user-modal">
                    <DialogHeader>
                      <DialogTitle>Crear Usuario</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Nombre</Label>
                        <Input
                          value={newUser.nombre}
                          onChange={(e) => setNewUser({ ...newUser, nombre: e.target.value })}
                          placeholder="Juan Pérez"
                          data-testid="user-nombre-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Usuario</Label>
                        <Input
                          value={newUser.username}
                          onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                          placeholder="juanperez"
                          data-testid="user-username-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Contraseña</Label>
                        <Input
                          type="password"
                          value={newUser.password}
                          onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                          placeholder="••••••••"
                          data-testid="user-password-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Rol</Label>
                        <Select value={newUser.role} onValueChange={(value) => setNewUser({ ...newUser, role: value })}>
                          <SelectTrigger data-testid="user-role-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="comercial">Comercial</SelectItem>
                            <SelectItem value="admin">Administrador</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button onClick={handleCreateUser} className="w-full" data-testid="create-user-button">
                        Crear Usuario
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Rol</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usuarios.map((usuario) => (
                    <TableRow key={usuario.id}>
                      <TableCell className="font-medium">{usuario.nombre}</TableCell>
                      <TableCell>{usuario.username}</TableCell>
                      <TableCell>
                        <Badge variant={usuario.role === 'admin' ? 'default' : 'secondary'}>
                          {usuario.role === 'admin' ? 'Admin' : 'Comercial'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(usuario.id)}
                          disabled={usuario.id === user.id}
                          data-testid={`delete-user-${usuario.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="clientes" className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold">Gestión de Clientes</h2>
                <div className="flex gap-4">
                  <Input
                    placeholder="Buscar cliente..."
                    value={searchCliente}
                    onChange={(e) => setSearchCliente(e.target.value)}
                    className="w-64"
                    data-testid="search-cliente-input"
                  />
                  <Dialog open={showImportCSVModal} onOpenChange={setShowImportCSVModal}>
                    <DialogTrigger asChild>
                      <Button variant="outline" data-testid="import-csv-button">
                        <Plus className="h-4 w-4 mr-2" />
                        Importar CSV
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="import-csv-modal">
                      <DialogHeader>
                        <DialogTitle>Importar Clientes desde CSV</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Archivo CSV</Label>
                          <Input
                            type="file"
                            accept=".csv"
                            onChange={(e) => setCsvFile(e.target.files[0])}
                            data-testid="csv-file-input"
                          />
                          <p className="text-xs text-muted-foreground">
                            El archivo debe ser un CSV con una columna llamada "nombre" o la primera columna con los nombres de clientes.
                          </p>
                        </div>
                        
                        <div className="bg-muted p-3 rounded-md text-sm space-y-1">
                          <p className="font-medium">Formato del CSV:</p>
                          <pre className="text-xs">
                            nombre
                            Cliente 1
                            Cliente 2
                            Cliente 3
                          </pre>
                        </div>

                        {csvFile && (
                          <div className="bg-accent/10 p-3 rounded-md text-sm">
                            <p className="font-medium">Archivo seleccionado:</p>
                            <p className="text-muted-foreground">{csvFile.name}</p>
                          </div>
                        )}

                        <Button 
                          onClick={handleImportCSV} 
                          className="w-full" 
                          disabled={!csvFile || importing}
                          data-testid="import-csv-submit"
                        >
                          {importing ? (
                            <span className="flex items-center">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Importando...
                            </span>
                          ) : (
                            'Importar Clientes'
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Dialog open={showClienteModal} onOpenChange={setShowClienteModal}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-cliente-button">
                        <Plus className="h-4 w-4 mr-2" />
                        Nuevo Cliente
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="cliente-modal">
                      <DialogHeader>
                        <DialogTitle>Crear Cliente</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>Nombre del Cliente</Label>
                          <Input
                            value={newCliente.nombre}
                            onChange={(e) => setNewCliente({ nombre: e.target.value })}
                            placeholder="Nombre del cliente"
                            data-testid="cliente-nombre-input"
                          />
                        </div>
                        <Button onClick={handleCreateCliente} className="w-full" data-testid="create-cliente-button">
                          Crear Cliente
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClientes.map((cliente) => (
                      <TableRow key={cliente.id}>
                        <TableCell className="font-medium">{cliente.nombre}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteCliente(cliente.id)}
                            data-testid={`delete-cliente-${cliente.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                Total: {filteredClientes.length} clientes
              </p>
            </div>
          </TabsContent>

          <TabsContent value="tipos" className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold">Tipos de Acción Comercial</h2>
                <Dialog open={showActionTypeModal} onOpenChange={setShowActionTypeModal}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-action-type-button">
                      <Plus className="h-4 w-4 mr-2" />
                      Nuevo Tipo
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="action-type-modal">
                    <DialogHeader>
                      <DialogTitle>Crear Tipo de Acción</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>Nombre</Label>
                        <Input
                          value={newActionType.nombre}
                          onChange={(e) => setNewActionType({ nombre: e.target.value })}
                          placeholder="Ej: Reunión, Propuesta"
                          data-testid="action-type-nombre-input"
                        />
                      </div>
                      <Button onClick={handleCreateActionType} className="w-full" data-testid="create-action-type-button">
                        Crear Tipo
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {actionTypes.map((type) => (
                    <TableRow key={type.id}>
                      <TableCell className="font-medium">
                        <Badge className={getBadgeClass(type.nombre)}>
                          {type.nombre}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteActionType(type.id)}
                          data-testid={`delete-action-type-${type.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Modal de Edición de Acción */}
      <Dialog open={showEditAccionModal} onOpenChange={setShowEditAccionModal}>
        <DialogContent data-testid="edit-action-admin-modal" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Acción Comercial</DialogTitle>
          </DialogHeader>
          {editingAccion && (
            <div className="space-y-4">
              {/* Checkbox para Posible Cliente */}
              <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <Checkbox
                  id="admin_edit_es_posible_cliente"
                  checked={editingAccion.es_posible_cliente || false}
                  onCheckedChange={(checked) => setEditingAccion({ 
                    ...editingAccion, 
                    es_posible_cliente: checked,
                    cliente_id: checked ? null : editingAccion.cliente_id
                  })}
                  data-testid="admin-edit-posible-cliente-checkbox"
                />
                <div className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4 text-blue-600" />
                  <Label htmlFor="admin_edit_es_posible_cliente" className="text-sm font-medium cursor-pointer">
                    Es un posible cliente
                  </Label>
                </div>
              </div>

              {editingAccion.es_posible_cliente ? (
                <div className="space-y-2">
                  <Label>Nombre del Posible Cliente *</Label>
                  <Input
                    value={editingAccion.posible_cliente_nombre || ''}
                    onChange={(e) => setEditingAccion({ ...editingAccion, posible_cliente_nombre: e.target.value })}
                    placeholder="Nombre del posible cliente"
                    data-testid="admin-edit-posible-cliente-nombre"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <ComboboxClientes
                    clientes={clientes}
                    value={editingAccion.cliente_id || ''}
                    onChange={(value) => setEditingAccion({ ...editingAccion, cliente_id: value })}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Tipo de Acción</Label>
                <Select
                  value={editingAccion.action_type_id}
                  onValueChange={(value) => setEditingAccion({ ...editingAccion, action_type_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {actionTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.nombre}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Horas</Label>
                  <Select
                    value={editingAccion.duracion_horas.toString()}
                    onValueChange={(value) => setEditingAccion({ ...editingAccion, duracion_horas: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[...Array(13)].map((_, i) => (
                        <SelectItem key={i} value={i.toString()}>{i}h</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Minutos</Label>
                  <Select
                    value={editingAccion.duracion_minutos.toString()}
                    onValueChange={(value) => setEditingAccion({ ...editingAccion, duracion_minutos: parseInt(value) })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55].map((min) => (
                        <SelectItem key={min} value={min.toString()}>{min}min</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Descripción</Label>
                <Textarea
                  value={editingAccion.descripcion}
                  onChange={(e) => setEditingAccion({ ...editingAccion, descripcion: e.target.value })}
                  rows={4}
                />
              </div>
              <div className="bg-muted p-3 rounded-md text-sm">
                <p className="font-medium">Información no editable:</p>
                <p className="text-muted-foreground">Comercial: {editingAccion.user_nombre}</p>
                <p className="text-muted-foreground">Fecha: {formatDate(editingAccion.fecha_hora)}</p>
                <p className="text-muted-foreground">GPS: {editingAccion.lat.toFixed(6)}, {editingAccion.lng.toFixed(6)}</p>
              </div>
              <Button onClick={handleUpdateAccion} className="w-full" data-testid="update-action-admin-button">
                Actualizar Acción
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardAdmin;
